import React from 'react';

import { useFormikContext } from 'formik';
import ValidatorFormGroup from '../../main/validator-form-group';
import { FormControl } from 'react-bootstrap';
import { DEF_VALIDATOR, validateEmail } from '../../../util/validators';
import isValidIbanNumber from '../../../util/iban_check';
import useTranslate from '../../useTranslate';

const REQUIRED = true;
const OPTIONAL = false;

export enum SettlementDetailsFields {
    holder = 'holder',
    iban = 'iban',
    bic = 'bic',
    emailAddress = 'emailAddress',
}

export type SettlementDetailsValues = {
    useSettlement: boolean;
    useEmail: boolean;
    creditNotesEnabled: boolean;
} & {
    [key in SettlementDetailsFields]: string;
};

const VALIDATORS = {
    holder: {
        isValid(val) {
            return !!val && DEF_VALIDATOR.test(val);
        },
    },
    iban: {
        isValid(val) {
            return !!val && isValidIbanNumber(val) === 1;
        },
    },
    bic: {
        isValid(val) {
            return (
                !val ||
                /^([a-zA-Z]){4}([a-zA-Z]){2}([0-9a-zA-Z]){2}([0-9a-zA-Z]{3})?$/.test(
                    val
                )
            );
        },
    },
    emailAddress: {
        isValid(val) {
            return validateEmail(val);
        },
    },
};

export function validateSettlementDetails(values: SettlementDetailsValues) {
    const errors = {};

    if (values.useSettlement) {
        [
            SettlementDetailsFields.holder,
            SettlementDetailsFields.iban,
            SettlementDetailsFields.bic,
        ].forEach(validateField);
    }
    if (values.useEmail) {
        validateField(SettlementDetailsFields.emailAddress);
    }

    return errors;

    function validateField(field: SettlementDetailsFields) {
        if (!VALIDATORS[field].isValid(values[field])) {
            errors[field] = true;
        }
    }
}

interface Props {
    disabled?: boolean;
    internalManager: boolean;
}

type FieldToString = { [key in SettlementDetailsFields]: string };

function ProfileSettlementDetailsForm(props: Readonly<Props>) {
    const strings = useTranslate('ProfileBillingDataForm');
    const formProps = useFormikContext<SettlementDetailsValues>();

    const labels: FieldToString = {
        [SettlementDetailsFields.holder]: strings.accountholderName,
        [SettlementDetailsFields.iban]: strings.iban,
        [SettlementDetailsFields.bic]: strings.bic,
        [SettlementDetailsFields.emailAddress]: strings.emailAddress,
    };
    const errorLabels: FieldToString = {
        [SettlementDetailsFields.holder]: strings.accountholderNameError,
        [SettlementDetailsFields.iban]: strings.ibanError,
        [SettlementDetailsFields.bic]: strings.bicError,
        [SettlementDetailsFields.emailAddress]: strings.emailAddressError,
    };
    const hintLabels: Partial<FieldToString> = {
        [SettlementDetailsFields.bic]: strings.bicHint,
    };

    return (
        <div>
            {renderUseSettlementCheckbox()}
            {formProps.values.useSettlement && (
                <>
                    {renderField(SettlementDetailsFields.holder, REQUIRED)}
                    {renderField(SettlementDetailsFields.iban, REQUIRED)}
                    {renderField(SettlementDetailsFields.bic, OPTIONAL)}
                </>
            )}
            {renderUseEmailCheckbox()}
            {formProps.values.useEmail && (
                <>
                    {renderField(
                        SettlementDetailsFields.emailAddress,
                        REQUIRED
                    )}
                </>
            )}
            {props.internalManager && renderCreditNotesEnabledCheckbox()}
        </div>
    );

    function renderField(field: SettlementDetailsFields, required: boolean) {
        return (
            <ValidatorFormGroup
                label={labels[field]}
                hideErrorLabel={!formProps.errors[field]}
                errorLabel={errorLabels[field]}
                helpLabel={hintLabels[field]}
            >
                <FormControl
                    type={'text'}
                    className={'input-text-color'}
                    value={formProps.values[field]}
                    name={field}
                    onChange={handleChange}
                    required={required}
                    disabled={props.disabled}
                />
            </ValidatorFormGroup>
        );

        function handleChange(e) {
            formProps.setFieldTouched(field, true, false).then();
            if (
                field === SettlementDetailsFields.iban &&
                !formProps.touched[SettlementDetailsFields.bic]
            ) {
                formProps.setFieldValue(SettlementDetailsFields.bic, '', false).then();
            }
            formProps.handleChange(e);
        }
    }

    function renderUseSettlementCheckbox() {
        return (
            <div className="checkbox checkbox-primary checkbox-custom">
                <input
                    type="checkbox"
                    id="useSettlement"
                    className="styled"
                    checked={formProps.values.useSettlement}
                    onChange={formProps.handleChange}
                    name={'useSettlement'}
                    disabled={props.disabled}
                />
                <label htmlFor="useSettlement">{strings.useSettlement}</label>
            </div>
        );
    }

    function renderUseEmailCheckbox() {
        return (
            <div className="checkbox checkbox-primary checkbox-custom">
                <input
                    type="checkbox"
                    id="useEmail"
                    className="styled"
                    checked={formProps.values.useEmail}
                    onChange={formProps.handleChange}
                    name={'useEmail'}
                    disabled={props.disabled}
                />
                <label htmlFor="useEmail">{strings.useEmail}</label>
            </div>
        );
    }

    function renderCreditNotesEnabledCheckbox() {
        return (
            <div className="checkbox checkbox-primary checkbox-custom">
                <input
                    type="checkbox"
                    id="creditNotesEnabled"
                    className="styled"
                    checked={formProps.values.creditNotesEnabled}
                    onChange={formProps.handleChange}
                    name={'creditNotesEnabled'}
                    disabled={props.disabled}
                />
                <label htmlFor="creditNotesEnabled">
                    {strings.creditNotesEnabled}
                </label>
            </div>
        );
    }
}

export default ProfileSettlementDetailsForm;
